import React, { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { InView } from 'react-intersection-observer';
import isEmpty from 'lodash/isEmpty';

import { fetchComingSoonPage } from 'Redux/ducks/algolia-calls';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import HitCard from 'Components/Cards/HitCard';
import Loading from 'Components/Utility/Loading';
import Meta from 'Components/Utility/SEO/Meta';
import NotFound from 'Components/NotFound/NotFound';
import PillFilters from 'Components/Utility/PillFilters';
import TakeOver from 'Components/Banner/TakeOver';
import sortByMessages from 'Components/Utility/SortBy/SortByMessages';


function AlgoliaComingSoon({ intl: { formatMessage } }) {
  const [hits, setHits] = useState({ available: [], presale: [] });
  const [type, setType] = useState('all');
  const [sort, setSort] = useState('');
  const [hasLoaded, setHasloaded] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    fetchComingSoonPage(sort).then((data) => {
      setHits(data);
      setHasloaded(true);
    }).catch(setFetchError);
  }, [sort]);

  const { available, presale } = hits;

  if (fetchError) {
    setTimeout(() => {
      window.prerenderReady = true;
    }, 1500);
    throw fetchError;
  }

  if (isEmpty(available) && isEmpty(presale) && hasLoaded) return <NotFound />;

  if (!hasLoaded) {
    return <Loading />;
  }

  const messages = defineMessages({
    title: {
      id: 'upcominggames.seotitle',
      defaultMessage: 'Upcoming Games | Pre-order Top Upcoming PC Games',
    },
    desc: {
      id: 'upcominggames.seodesc',
      defaultMessage: 'Pre-order the biggest and best upcoming games - Buy now and be the first to play on release day. With the latest games from Bethesda, Warner Bros, 2K, SEGA and more, there\'s plenty to get excited about!',
    },
    all: {
      id: 'upcominggames.all',
      defaultMessage: 'All',
    },
    availabletopreorder: {
      id: 'upcominggames.availabletopreorder',
      defaultMessage: 'Available to Preorder',
    },
    availablesoon: {
      id: 'upcominggames.availablesoon',
      defaultMessage: 'Available Soon',
    },
  });

  const onFilterChange = (value) => {
    setType(value);
  };

  const filters = [
    { name: formatMessage(messages.all), type: 'all', live: true },
    { name: formatMessage(messages.availabletopreorder), type: 'available', live: !isEmpty(available) },
    { name: formatMessage(messages.availablesoon), type: 'presale', live: !isEmpty(presale) },
  ].filter(t => t.live);

  let hitsToDisplay = available.concat(presale);
  if (type === 'available') hitsToDisplay = available;
  if (type === 'presale') hitsToDisplay = presale;

  return (
    <React.Fragment>
      <TakeOver appearsOn="coming_soon" />

      <div className="coming-soon-page container">
        <Meta
          brandTitle
          title={formatMessage(messages.title)}
          description={formatMessage(messages.desc)}
          url="upcoming-games"
          hits={available}
          pageType="CollectionPage"
        />

        <InView>
          {({ inView, ref }) =>
            <div ref={ref}>
              <h1 className="pt-5 pb-2">
                <FormattedMessage id="comingsoon.header" defaultMessage="Upcoming Games" />
              </h1>
              {!inView &&
                <div className="d-block d-sm-none">
                  <Backtotopbutton />
                </div>
              }
            </div>
          }
        </InView>

        <p className="seo-blurb-text mb-4">
          <FormattedMessage
            id="upcominggames.intro"
            defaultMessage="Find all upcoming games and grab some sweet bonuses when you pre-order. You can also use the dropdown menu to sort by Top Sellers, Release Date, or Price! Pre-order or wishlist for later so you don’t miss out on release day."
          />
        </p>

        <div className="categories-and-sortby-section d-flex">
          <PillFilters value={type} options={filters} onChange={onFilterChange} />
          <div className="filters-container">
            <div className="mr-3"><FormattedMessage id="upcominggames.sortby" defaultMessage="Sort by" />:</div>
            <div className="dropdown-container sort-by">
              <select value={sort} onChange={e => setSort(e.target.value)}>
                <option value="">{formatMessage(sortByMessages.topSellers)}</option>
                <option value={'price_asc'}>{formatMessage(sortByMessages.priceLowHigh)}</option>
                <option value={'price_desc'}>{formatMessage(sortByMessages.priceHighLow)}</option>
                <option value={'name'}>{formatMessage(sortByMessages.alphabetical)}</option>
                <option value={'discount'}>{formatMessage(sortByMessages.topDiscount)}</option>
                <option value={'latest_deals'}>{formatMessage(sortByMessages.latestDeals)}</option>
                <option value={'ending_soon'}>{formatMessage(sortByMessages.endingSoon)}</option>
                <option value={'most_wanted'}>{formatMessage(sortByMessages.mostWanted)}</option>
                <option value={'release_date_asc'}>{formatMessage(sortByMessages.releaseDateShort)}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="d-none d-xl-block">
          <Row>
            <Col xl={6}>
              <Row>
                {hitsToDisplay.slice(0, 1).map(hit =>
                  <HitCard
                    bigCard
                    key={hit.objectID}
                    hit={hit}
                    xsSize={12}
                    mdSize={12}
                    lgSize={12}
                    sizesOverride="(min-width: 1520px) 645px, (min-width: 1200px) 550px, (min-width: 992px) 460px, (min-width: 768px) 340px, (min-width: 576px) 255px, 50vw"
                    hitCardNameTag="h2"
                  />,
                )}
              </Row>
            </Col>
            <Col xl={6}>
              <Row>
                {hitsToDisplay.slice(1, 5).map(hit =>
                  <HitCard
                    key={hit.objectID}
                    hit={hit}
                    xsSize={6}
                    mdSize={6}
                    lgSize={6}
                    hitCardNameTag="h2"
                  />,
                )}
              </Row>
            </Col>
          </Row>
          <Row>
            <Col xl={6}>
              <Row>
                {hitsToDisplay.slice(5, 9).map(hit =>
                  <HitCard
                    key={hit.objectID}
                    hit={hit}
                    xsSize={6}
                    mdSize={6}
                    lgSize={6}
                    hitCardNameTag="h2"
                  />,
                )}
              </Row>
            </Col>
            <Col xl={6}>
              <Row>
                {hitsToDisplay.slice(9, 10).map(hit =>
                  <HitCard
                    bigCard
                    key={hit.objectID}
                    hit={hit}
                    xsSize={12}
                    mdSize={12}
                    lgSize={12}
                    sizesOverride="(min-width: 1520px) 645px, (min-width: 1200px) 550px, (min-width: 992px) 460px, (min-width: 768px) 340px, (min-width: 576px) 255px, 50vw"
                    hitCardNameTag="h2"
                  />,
                )}
              </Row>
            </Col>
          </Row>
          <Row>
            {hitsToDisplay.slice(10).map(hit =>
              <HitCard
                key={hit.objectID}
                hit={hit}
                lgSize={3}
                hitCardNameTag="h2"
              />,
            )}
          </Row>
        </div>


        <Row className="d-flex d-xl-none">
          {hitsToDisplay.map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              hitCardNameTag="h2"
            />,
          )}
        </Row>


        <p className="seo-blurb-text mb-5">
          <FormattedMessage id="comingsoon.longintro" defaultMessage="From the biggest announcements at the world's top gaming conventions to the most talked about Indie titles, pre-order the latest and greatest PC games and be the first to play on release day. With the latest games from Bethesda, Warner Bros, 2K, SEGA and more, there's plenty to get excited about!" />
        </p>
      </div>
    </React.Fragment>
  );
}

export default injectIntl(AlgoliaComingSoon);
